import { useContext, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
  ACTIVE,
  COMPLETED,
  CURRENT_STEP,
  INACTIVE,
  ProgressBarContext,
  SHOW_PROGRESS_BAR,
  UPDATE_STEP,
  SET_STEP
} from "../ProgressBarProvider";

const matchPathWithParams = (pattern, path) => {
  const patternParts = pattern.split("/");
  const pathParts = path.split("/");

  if (patternParts.length !== pathParts.length) {
    return false;
  }

  return patternParts.every((part, index) => {
    return part.startsWith(":") || part === pathParts[index];
  });
};

export const useProgressBar = () => {
  const { state, dispatch } = useContext(ProgressBarContext);
  const { steps, showProgressBar, currentStepName } = state;
  const { pathname } = useLocation();

  // Use ref to track if we've already processed these steps/pathname combination
  const processedRef = useRef({ pathname: "", stepCount: 0, currentStepIndex: -1 });
  // Memoize currentStepIndex to prevent recalculation on every render
  const currentStepIndex = useMemo(() => {
    // Early return if steps is null, undefined, or not an array
    if (!steps || !Array.isArray(steps)) {
      return -1;
    }

    return steps.findIndex(step =>
      step?.substeps?.some(substep => matchPathWithParams(substep, pathname))
    );
  }, [steps, pathname]); // Single useEffect to handle all progress bar updates

  useEffect(() => {
    // Early return if steps are not ready or empty
    if (!steps || steps.length === 0) {
      return;
    }

    // Skip if we've already processed this pathname and step count combination
    if (
      processedRef.current.pathname === pathname &&
      processedRef.current.stepCount === steps.length &&
      processedRef.current.currentStepIndex === currentStepIndex
    ) {
      return;
    }

    // Update the processed reference
    processedRef.current = {
      pathname,
      stepCount: steps.length,
      currentStepIndex
    };

    // Set current route for step determination
    dispatch({
      type: SET_STEP,
      payload: {
        currentRoute: pathname
      }
    });

    // Show/hide progress bar based on current step
    dispatch({
      type: SHOW_PROGRESS_BAR,
      payload: currentStepIndex
    });

    // Early return if no valid step found
    if (currentStepIndex === -1) {
      return;
    }

    // Set current step name
    const stepName = steps[currentStepIndex]?.name;
    if (stepName) {
      dispatch({
        type: CURRENT_STEP,
        payload: stepName
      });
    }

    // Update all steps status in batch to reduce dispatch calls
    const stepUpdates = [];

    for (let i = 0; i < steps.length; i++) {
      if (i < currentStepIndex) {
        // Completed steps
        stepUpdates.push({
          type: UPDATE_STEP,
          payload: {
            index: i,
            value: 100,
            status: COMPLETED,
            subStepIndex: steps[i].totalSteps - 1
          }
        });
      } else if (i === currentStepIndex) {
        // Current active step - always fully filled
        stepUpdates.push({
          type: UPDATE_STEP,
          payload: {
            index: i,
            value: 100,
            status: ACTIVE,
            subStepIndex: i
          }
        });
      } else {
        // Inactive steps
        stepUpdates.push({
          type: UPDATE_STEP,
          payload: {
            index: i,
            value: 0,
            status: INACTIVE,
            subStepIndex: 0
          }
        });
      }
    }

    // Dispatch all step updates in a single batch
    if (stepUpdates.length > 0) {
      stepUpdates.forEach(update => dispatch(update));
    }
  }, [pathname, currentStepIndex, dispatch, steps]); // Keep steps but optimize with guards

  return { showProgressBar, steps, currentStepName };
};
